<template>
  <BaseInput
    :id="id"
    ref="baseInputRef"
    class="password"
    :value="value"
    :type="c_type"
    :name="name"
    :placeholder="c_placeholder"
    :disabled="disabled"
    :maxlength="maxlength"
    :error-messages="errorMessages"
    data-id="register-password-input"
    @input="onInput($event)"
  >
    <template #body-right>
      <button
        type="button"
        class="input-wrapper__show_password"
        data-id="register-show-password-click"
        :class="{ 'active': isShowPassword }"
        @click="isShowPassword = !isShowPassword"
      >
        <SvgIcon class="input-wrapper__icon" :name="isShowPassword ? 'login/eye-slash': 'login/eye'" />
      </button>
    </template>
    <template #footer>
      <div v-if="value.length >= minlength && passwordStrength" class="password-strength">
        <div class="password-strength__info">
          <div
            class="password-strength__level"
            :class="{
              'weak': strength === 1,
              'average': strength === 2,
              'strong': strength >= 3,
            }"
          />
          <UiText
            class="password-strength__text"
            :text="textStrength"
            font="body-caption"
            color="form-hint-text-color-info"
          />
          <SvgIcon
            v-click-outside="() => isShowPasswordTooltip = false"
            class="password-strength__icon"
            :class="{ 'active': isShowPasswordTooltip }"
            name="common/info-italic"
            @click="isShowPasswordTooltip = !isShowPasswordTooltip"
          />
          <div
            v-show="isShowPasswordTooltip"
            class="password-strength__tooltip"
          >
            <div class="password-strength__list">
              <UiText
                :text="$tc('forms.passwordInfo.minLength')"
                font="body-caption"
                class="password-strength__item"
                :class="{ 'valid': value.length >= minlength }"
              />
              <UiText
                :text="$tc('forms.passwordInfo.latinLowerCase')"
                font="body-caption"
                class="password-strength__item "
                :class="{ 'valid': isContainsLowerCase }"
              />
              <UiText
                :text="$tc('forms.passwordInfo.latinUpperCase')"
                font="body-caption"
                class="password-strength__item"
                :class="{ 'valid': isContainsUpperCase }"
              />
              <UiText
                :text="$tc('forms.passwordInfo.symbols')"
                font="body-caption"
                class="password-strength__item"
                :class="{ 'valid': isContainsSymbol }"
              />
              <UiText
                :text="$tc('forms.passwordInfo.digits')"
                font="body-caption"
                class="password-strength__item"
                :class="{ 'valid': isContainsDigit }"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseInput>
</template>

<script>
import BaseInput from '@/components/Base/Input'
import ClickOutside from 'vue-click-outside'

const REG_EXP = /^[A-Za-z0-9.,!#$%&'*+\-/=?^_`{|[\]~"(),:;<>@]+$/
const CLEANUP_REG_EXP = /[^A-Za-z0-9.,!#$%&'*+\-/=?^_`{|[\]~"(),:;<>@]/g

export default {
  name: 'BaseInputPassword',
  components: { BaseInput },
  directives: { ClickOutside },
  props: {
    value: { type: String, required: false },
    name: { type: String },
    // placeholder have defaults: email, password, tel; to disable default -> placeholder=""
    placeholder: { type: String },
    disabled: { type: Boolean, default: false },
    id: { type: String, required: false }, // don't need id by default, have uId
    maxlength: { type: Number, default: 70 },
    minlength: { type: Number, default: 6 },
    passwordStrength: { type: Boolean },
    showPassword: { type: Boolean },
    errorMessages: { type: Array },
  },
  data() {
    return {
      isShowPassword: false,
      isShowPasswordTooltip: false,
    }
  },
  computed: {
    textStrength() {
      switch (this.strength) {
      case 1:
        return this.$t('forms.password.weak')
      case 2:
        return this.$t('forms.password.average')
      case 3:
        return this.$t('forms.password.strong')
      default: return ''
      }
    },
    c_placeholder() {
      if (this.placeholder !== undefined) return this.placeholder
      return this.$t('forms.placeholder.password')
    },
    c_type() {
      return this.isShowPassword ? 'text' : 'password'
    },
    isContainsLowerCase() {
      return /[a-z]/.test(this.value)
    },
    isContainsUpperCase() {
      return /[A-Z]/.test(this.value)
    },
    isContainsSymbol() {
      return /[!@#$%^&*(){}[\]<>.,\\\/?~`=+\-_|"':;]/.test(this.value)
    },
    isContainsDigit() {
      return /[0-9]/.test(this.value)
    },
    strength() {
      if (this.value.length < this.minlength) return 0
      const isStrong
        = this.isContainsLowerCase
        && this.isContainsUpperCase
        && this.isContainsSymbol
        && this.isContainsDigit
      if (isStrong) return 3
      const isAverage
        = (this.isContainsLowerCase && this.isContainsUpperCase)
        || (this.isContainsLowerCase && this.isContainsSymbol)
        || (this.isContainsLowerCase && this.isContainsDigit)
        || (this.isContainsUpperCase && this.isContainsSymbol)
        || (this.isContainsUpperCase && this.isContainsDigit)
        || (this.isContainsSymbol && this.isContainsDigit)
      if (isAverage) return 2
      if (this.value.length >= this.minlength) return 1
      else return 0
    },
  },
  mounted() {
    if (this.showPassword) this.isShowPassword = true
  },
  methods: {
    onInput(value) {
      const cleanValue = value.match(REG_EXP) ? value : value.replace(CLEANUP_REG_EXP, '')
      this.$refs.baseInputRef.getInput().value = cleanValue

      if (cleanValue !== this.value)
        this.$emit('input', cleanValue)
    },
  },
}
</script>

<style lang="scss" src="./style.scss" scoped></style>
